/**
 * ConversionReferences — Liquid 변환 레퍼런스 관리
 *
 * 목록 + 편집 Drawer (Monaco 3개 탭: header / referenceHtml / footer)
 */

import { useState, useCallback } from 'react'
import Editor from '@monaco-editor/react'
import { useApiFetch } from '../hooks/useApiFetch'
import { useMonacoModelCleanup } from '../hooks/useMonacoModelCleanup'
import { useToast } from '../components/ToastContainer'
import { apiFetch } from '../lib/api'
import { Button } from '../ui/Button'
import { Tabs } from '../ui/Tabs'
import { Input } from '../ui/Input'
import { Textarea } from '../ui/Textarea'
import { Badge } from '../ui/Badge'
import { Drawer } from '../ui/Drawer'
import { ConfirmDialog } from '../ui/ConfirmDialog'
import styles from './ConversionReferences.module.css'

interface ConversionReference {
  id: string
  slug: string
  name: string
  description: string | null
  systemPromptHeader: string
  referenceHtml: string
  systemPromptFooter: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

type EditorTab = 'header' | 'html' | 'footer'

const EMPTY_FORM = {
  slug: '',
  name: '',
  description: '',
  systemPromptHeader: '',
  referenceHtml: '',
  systemPromptFooter: '',
}

export function ConversionReferences() {
  const { addToast } = useToast()
  const { data, isLoading, refetch } = useApiFetch<{ references: ConversionReference[] }>(
    '/api/admin/conversion-references',
  )
  const references = data?.references ?? []

  const [editing, setEditing] = useState<ConversionReference | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [activeTab, setActiveTab] = useState<EditorTab>('header')
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ConversionReference | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [seeding, setSeeding] = useState(false)

  const openCreate = useCallback(() => {
    setIsCreating(true)
    setEditing(null)
    setForm(EMPTY_FORM)
    setActiveTab('header')
  }, [])

  const openEdit = useCallback((ref: ConversionReference) => {
    setIsCreating(false)
    setEditing(ref)
    setForm({
      slug: ref.slug,
      name: ref.name,
      description: ref.description ?? '',
      systemPromptHeader: ref.systemPromptHeader,
      referenceHtml: ref.referenceHtml,
      systemPromptFooter: ref.systemPromptFooter,
    })
    setActiveTab('header')
  }, [])

  const closeDrawer = useCallback(() => {
    setEditing(null)
    setIsCreating(false)
  }, [])

  const handleSave = useCallback(async () => {
    if (!form.slug.trim() || !form.name.trim()) {
      addToast({ type: 'error', message: 'slug/name은 필수입니다' })
      return
    }
    if (!form.systemPromptHeader.trim() || !form.systemPromptFooter.trim()) {
      addToast({ type: 'error', message: '헤더/푸터는 비워둘 수 없습니다' })
      return
    }
    setSaving(true)
    try {
      const payload = {
        slug: form.slug.trim(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        systemPromptHeader: form.systemPromptHeader,
        referenceHtml: form.referenceHtml,
        systemPromptFooter: form.systemPromptFooter,
      }
      if (isCreating) {
        await apiFetch('/api/admin/conversion-references', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
        addToast({ type: 'success', message: '레퍼런스가 생성되었습니다' })
      } else if (editing) {
        await apiFetch(`/api/admin/conversion-references/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
        addToast({ type: 'success', message: '저장되었습니다' })
      }
      closeDrawer()
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '저장 실패' })
    } finally {
      setSaving(false)
    }
  }, [form, isCreating, editing, addToast, refetch, closeDrawer])

  const handleActivate = useCallback(async (id: string) => {
    try {
      await apiFetch(`/api/admin/conversion-references/${id}/activate`, { method: 'POST' })
      addToast({ type: 'success', message: '활성화되었습니다' })
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '활성화 실패' })
    }
  }, [addToast, refetch])

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await apiFetch(`/api/admin/conversion-references/${deleteTarget.id}`, { method: 'DELETE' })
      addToast({ type: 'success', message: '삭제되었습니다' })
      setDeleteTarget(null)
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '삭제 실패' })
    } finally {
      setDeleting(false)
    }
  }, [deleteTarget, addToast, refetch])

  const handleDuplicate = useCallback((ref: ConversionReference) => {
    setIsCreating(true)
    setEditing(null)
    setForm({
      slug: `${ref.slug}-copy`,
      name: `${ref.name} (복사본)`,
      description: ref.description ?? '',
      systemPromptHeader: ref.systemPromptHeader,
      referenceHtml: ref.referenceHtml,
      systemPromptFooter: ref.systemPromptFooter,
    })
    setActiveTab('header')
  }, [])

  const handleSeedDefault = useCallback(async () => {
    setSeeding(true)
    try {
      const res = await apiFetch<{ seeded: Array<{ slug: string; updated: boolean }> }>(
        '/api/admin/conversion-references/seed-default',
        { method: 'POST' },
      )
      const lines = res.seeded.map((s) => `${s.slug} ${s.updated ? '갱신' : '신규 seed'}`)
      addToast({ type: 'success', message: lines.join(' · ') })
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || 'seed 실패' })
    } finally {
      setSeeding(false)
    }
  }, [addToast, refetch])

  // 드로어 내 Monaco value/setter
  const editorValue = form[
    activeTab === 'header' ? 'systemPromptHeader'
    : activeTab === 'html' ? 'referenceHtml'
    : 'systemPromptFooter'
  ]
  const editorLanguage = activeTab === 'html' ? 'html' : 'markdown'
  // 탭마다 다른 path(=모델 URI). path 가 없으면 세 탭이 모델 하나를 공유해서,
  // 언어 전환 시 이전 언어의 비동기 검증 결과가 현재 내용에 허위 마커로 남는다.
  // 편집 대상 id 를 경로에 포함해야 한다 — 언마운트 시 dispose 되는 건 그때 붙어있던
  // 모델뿐이라, id 가 없으면 드로어를 닫고 다른 레퍼런스를 열었을 때 비활성 탭의
  // 낡은 모델이 같은 path 로 재사용돼 이전 항목 내용이 그대로 뜬다.
  const editorKey = editing?.id ?? 'new'
  const editorPath = `conversion-reference/${editorKey}/${activeTab}.${activeTab === 'html' ? 'html' : 'md'}`
  useMonacoModelCleanup(`conversion-reference/${editorKey}/`)

  const setEditorValue = useCallback((v: string | undefined) => {
    const val = v ?? ''
    setForm((prev) => ({
      ...prev,
      systemPromptHeader: activeTab === 'header' ? val : prev.systemPromptHeader,
      referenceHtml: activeTab === 'html' ? val : prev.referenceHtml,
      systemPromptFooter: activeTab === 'footer' ? val : prev.systemPromptFooter,
    }))
  }, [activeTab])

  const isOpen = isCreating || editing !== null

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Liquid 변환 레퍼런스</h1>
        <div className={styles.headerActions}>
          <Button variant="outline" size="sm" onClick={handleSeedDefault} disabled={seeding}>
            {seeding ? 'seed 중...' : '기본 레퍼런스 seed'}
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate}>새 레퍼런스</Button>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loading}>로딩 중...</div>
      ) : references.length === 0 ? (
        <div className={styles.empty}>
          등록된 레퍼런스가 없습니다. "기본 레퍼런스 seed" 로 stepwise-bind-v1 을 생성하세요.
        </div>
      ) : (
        <div className={styles.list}>
          {references.map((ref) => (
            <div key={ref.id} className={styles.item}>
              <div className={styles.itemMain}>
                <div className={styles.itemHeader}>
                  <span className={styles.itemName}>{ref.name}</span>
                  <span className={styles.itemSlug}>{ref.slug}</span>
                  {ref.isActive && <Badge color="green">활성</Badge>}
                </div>
                {ref.description && <p className={styles.itemDesc}>{ref.description}</p>}
                <p className={styles.itemMeta}>
                  업데이트: {new Date(ref.updatedAt).toLocaleString('ko-KR')}
                </p>
              </div>
              <div className={styles.itemActions}>
                {!ref.isActive && (
                  <Button variant="outline" size="sm" onClick={() => handleActivate(ref.id)}>
                    활성화
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => handleDuplicate(ref)}>
                  복제
                </Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(ref)}>
                  편집
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteTarget(ref)}
                  disabled={ref.isActive}
                  title={ref.isActive ? '활성 레퍼런스는 삭제할 수 없습니다' : undefined}
                >
                  삭제
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={isOpen}
        onClose={closeDrawer}
        title={isCreating ? '새 레퍼런스' : `편집 — ${editing?.name ?? ''}`}
        size="lg"
        width="min(1100px, 92vw)"
      >
        <div className={styles.form}>
          <div className={styles.formRow}>
            <label className={styles.label}>slug</label>
            <Input
              value={form.slug}
              onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
              placeholder="drawing-v2"
            />
          </div>
          <div className={styles.formRow}>
            <label className={styles.label}>name</label>
            <Input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Drawing v2"
            />
          </div>
          <div className={styles.formRow}>
            <label className={styles.label}>description</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="이 레퍼런스의 목적"
              rows={2}
            />
          </div>

          <Tabs
            className={styles.editorTabs}
            size="sm"
            items={[
              { key: 'header', label: 'systemPromptHeader' },
              { key: 'html', label: 'referenceHtml' },
              { key: 'footer', label: 'systemPromptFooter' },
            ]}
            value={activeTab}
            onChange={(key) => setActiveTab(key as EditorTab)}
          />
          <div className={styles.editorWrap}>
            <Editor
              height="100%"
              path={editorPath}
              language={editorLanguage}
              value={editorValue}
              onChange={setEditorValue}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                wordWrap: 'on',
                tabSize: 2,
              }}
            />
          </div>

          <div className={styles.formActions}>
            <Button variant="ghost" onClick={closeDrawer}>취소</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          </div>
        </div>
      </Drawer>

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="레퍼런스 삭제"
        message={`"${deleteTarget?.name}" 레퍼런스를 삭제하시겠습니까? 관련 변환 이력의 참조는 NULL로 처리됩니다.`}
        confirmLabel="삭제"
        variant="danger"
        loading={deleting}
      />
    </div>
  )
}

